import * as React from "react";
import { Alert, AlertColor, Box, Stack } from "@mui/material";
import CclInfoTable, {
    CclInfoTableFormValue,
    CclInfoTableRowTemplate,
} from "../../../components/common/cclInfoTable/cclInfoTable";
import { eInfoTableValueFieldEditType } from "../../../components/common/cclInfoTable/cclInfoTableValueField";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { useAppDispatch } from "../../../app/hooks";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { cclTokenedPayrollApi } from "../../../services/cclTokenedPayrollApi";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import { CclInfoTblSessionPayrollOneTemplate } from "../../sessions/sessionDetail/payroll/sessionPayrollTemplateOne";
import { CclInfoTblSessionPayrollTwoTemplate } from "../../sessions/sessionDetail/payroll/sessionPayrollTemplateTwo";
import { PayrollProject } from "./payrollProjectDetailsTypes";

export type PayrollProjectSummaryProps = {
    project: PayrollProject;
    editable?: boolean;
};

type ProjectForm = {
    eventCode: string;
    projectName: string;
    lob: string;
    client: string;
    adminLoc: string;
    glGroup: string;
};

const toForm = (project: PayrollProject): ProjectForm => ({
    eventCode: String(project.projectCode ?? project.projectId),
    projectName: String(project.projectName ?? project.sessionTitle ?? ""),
    lob: String(project.lob ?? project.sessionType ?? ""),
    client: String(project.customer ?? ""),
    adminLoc: String(project.adminLoc ?? ""),
    glGroup: String(project.glGroup ?? ""),
});

const PayrollProjectSummary: React.FC<PayrollProjectSummaryProps> = ({ project, editable = false }) => {
    const [changes, setChanges] = React.useState<Partial<ProjectForm>>({});
    const [tableVersion, setTableVersion] = React.useState(0);
    const [result, setResult] = React.useState<{ message: string; severity: AlertColor } | null>(null);
    const { data: referenceData, isLoading: loadingReferences, isError: referenceError } =
        cclTokenedPayrollApi.useGetReferenceDataQuery();
    const { data: glData, isLoading: loadingGl, isError: glError } = cclTokenedPayrollApi.useGetGlDataQuery();
    const { data: clientData, isLoading: loadingClients, isError: clientError } =
        cclTokenedPayrollApi.useGetClientListQuery();
    const [updateProject, { isLoading: saving }] = cclTokenedPayrollApi.useUpdatePayrollProjectMutation();
    const dispatch = useAppDispatch();
    const { logEvent } = useLogAccessEvent();
    const claimsService = React.useMemo(() => new UserClaimsService(), []);
    const readOnly = !editable || claimsService.IsReadOnly();
    const data = React.useMemo(() => toForm(project), [project]);
    const form = { ...data, ...changes };

    const templates = React.useMemo(() => {
        const locations: any[] = (referenceData?.locations ?? []).filter(
            (location: any) => location.active !== false || String(location.locCode) === data.adminLoc
        );
        const groups = (glData?.glGroups ?? []).filter(
            (group) => group.active || group.groupCode === data.glGroup
        );
        const adminLocOptions = Array.from(new Map<string, { val: string; option: string }>(
            locations.map((location: any) => [String(location.locCode), {
                val: String(location.locCode),
                option: `${location.locCode} - ${location.locDesc}`,
            }])
        ).values()).sort((a, b) => a.val.localeCompare(b.val));
        const glGroupOptions = groups.map((group) => ({
            val: group.groupCode,
            option: `${group.groupCode} - ${group.groupDesc}`,
        })).sort((a, b) => a.val.localeCompare(b.val));
        const lobOptions = (referenceData?.linesOfBusiness ?? [])
            .filter((lob: any) => lob.active !== false || String(lob.lobCode) === data.lob)
            .map((lob: any) => ({
                val: String(lob.lobCode),
                option: `${lob.lobCode} - ${lob.lobDescription ?? lob.description ?? ""}`,
            }))
            .sort((a: { val: string }, b: { val: string }) => a.val.localeCompare(b.val));
        const clientNames: string[] = (Array.isArray(clientData) ? clientData : clientData?.data ?? [])
            .map((client: any) => String(client.clientName ?? "").trim())
            .filter((name: string) => name !== "");
        const clientOptions = Array.from(new Set(clientNames))
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ val: name, option: name }));
        const optionsByField: Record<string, Array<{ val: string; option: string }>> = {
            adminLoc: adminLocOptions,
            glGroup: glGroupOptions,
            lob: lobOptions,
            client: clientOptions,
        };

        // Preserve existing values even if the reference data is unavailable.
        Object.entries(optionsByField).forEach(([field, options]) => {
            const value = data[field as keyof ProjectForm];
            if (value && !options.some((option) => option.val === value)) {
                options.push({ val: value, option: value });
            }
        });

        const cloneTemplate = (template: CclInfoTableRowTemplate[]) => template.map((row) => ({
            ...row,
            editable: true,
            valueField: {
                ...row.valueField,
                editFieldType: optionsByField[row.id] ? eInfoTableValueFieldEditType.Select
                    : eInfoTableValueFieldEditType.TextInput,
                selectOptions: optionsByField[row.id] ?? null,
            },
        }));

        return [cloneTemplate(CclInfoTblSessionPayrollOneTemplate), cloneTemplate(CclInfoTblSessionPayrollTwoTemplate)];
    }, [referenceData, glData, clientData, data]);

    const handleChange = (values: CclInfoTableFormValue[]) => {
        setChanges((current) => {
            const updated = { ...current };
            values.forEach(({ name, value }) => {
                if (Object.prototype.hasOwnProperty.call(data, name)) {
                    updated[name as keyof ProjectForm] = String(
                        value != null && typeof value === "object" && "val" in value ? value.val ?? "" : value ?? ""
                    );
                }
            });
            return updated;
        });
    };

    const handleSave = async () => {
        if (readOnly || saving || !form.adminLoc.trim() || !form.glGroup.trim()) {
            return;
        }
        try {
            await updateProject({ ...form, projectId: project.projectId }).unwrap();
            logEvent("PayrollProjectEdited", {
                sessionID: project.sessionId ?? "",
                projectCode: form.eventCode,
            });
            dispatch(cclTokenedPayrollApi.util.invalidateTags(["miscProjects"]));
            setChanges({});
            setTableVersion((version) => version + 1);
            setResult({ message: "Payroll Project Saved", severity: "success" });
        } catch (error) {
            console.error("Failed to update payroll project:", error);
            setResult({ message: "Failed to save Payroll Summary", severity: "error" });
        }
    };

    const hasChanges = Object.keys(changes).length > 0;

    return (
        <>
            {editable && (referenceError || glError || clientError) && (
                <Alert severity="warning" sx={{ m: 2 }}>Unable to load some project options. Existing values are preserved.</Alert>
            )}
            <Stack direction="row" spacing={2} sx={{ width: 1, height: 1, p: 2 }}>
                <Box sx={{ width: 1, height: 1, p: 2 }}>
                    <CclInfoTable
                        key={`left-${tableVersion}`}
                        template={templates[0]}
                        data={data}
                        readonly={readOnly}
                        hideSave
                        onChange={handleChange}
                    />
                </Box>
                <Box sx={{ width: 1, height: 1, p: 2 }}>
                    <CclInfoTable
                        key={`right-${tableVersion}`}
                        template={templates[1]}
                        data={data}
                        readonly={readOnly}
                        hideSave
                        onChange={handleChange}
                        miscButton={!readOnly ? (
                            <>
                                {hasChanges && <Alert severity="info" sx={{ width: "100%" }}>You have pending changes. Press Save to commit them.</Alert>}
                                <CclLoadingButton
                                    sx={{ float: "right", height: 36 }}
                                    size="small"
                                    mode="primary"
                                    loading={saving}
                                    disabled={saving || loadingReferences || loadingGl || loadingClients || !hasChanges || !form.adminLoc.trim() || !form.glGroup.trim()}
                                    onClick={handleSave}
                                >
                                    Save
                                </CclLoadingButton>
                            </>
                        ) : null}
                    />
                </Box>
            </Stack>
            <CclAlertSnackbar open={Boolean(result)} message={result?.message ?? ""}
                severity={result?.severity} onClose={() => setResult(null)} />
        </>
    );
};

export default PayrollProjectSummary;